import { apply } from 'json-logic-js';
import { translations } from '../translations/translations';
import type { Language } from '../types/translations';
import type {
    Action,
    AuthenticationStatusResponse,
    AutomationTaskEdit,
    Connector,
    Option,
    OptionValueType,
    TaskItem,
    Trigger,
    Validation,
} from '../types/types';

const hexChars = '0123456789abcdef';

const randomHex = () => hexChars[Math.floor(Math.random() * 16)];

export const newGuid = () =>
    [...'00000000-0000-0000-0000-000000000000']
        .map((char) => (char === '0' ? randomHex() : char))
        .join('');

/** Turn the the base64 SVG into an image src.
 *
 * TODO:
 * Replace the '' with some sort of a default icon if no other given,
 * or send the default also from the API? */
export const base64SvgToSrc = (base64Svg: string): string =>
    base64Svg ? `data:image/svg+xml;base64,${base64Svg}` : '';

export const base64JpgToSrc = (base64Jpg: string): string =>
    base64Jpg ? `data:image/jpeg;base64,${base64Jpg}` : '';

export const base64toFile = (base64: string, filename: string): File => {
    const dataUrl = base64JpgToSrc(base64);
    const data = dataUrl.split(',');
    const mime = data[0].match(/:(.*?);/)![1];
    const decodedData = atob(data[data.length - 1]);
    let n = decodedData.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = decodedData.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
};

export const isString = (value: unknown): value is string => {
    return typeof value === 'string';
};

export const isArray = (value: unknown): value is [] => {
    return Array.isArray(value);
};

export const isObject = (value: unknown): value is Record<string, unknown> => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
};

// Escape string for use in a RegEx
// https://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript/3561711#3561711
export const escapeStringForRegex = (str: string) => str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

export const replaceAll = (value: string, substring: string, newSubstring: string) =>
    value.replace(new RegExp(escapeStringForRegex(substring), 'g'), newSubstring);

export const stringReplace = (template: string, ...replacementParameters: unknown[]) =>
    replacementParameters.length > 0
        ? (replacementParameters.reduce((reduceString, parameter) => {
              // / \${.*?} /
              // \$ matches the character $ literally (case sensitive)
              // { matches the character { literally (case sensitive)
              // .*? matches any character (except for line terminators)
              // *? Quantifier — Matches between zero and unlimited times, as few times as possible, expanding as needed (lazy)
              // } matches the character } literally (case sensitive)
              const valueReferenceRegEx = /\${.*?}/;

              // This function accepts different types of parameters
              // e.g. stringReplace('${test}','replaceFirstReference','replaceSecondReference')

              if (isString(reduceString) && isString(parameter)) {
                  return reduceString.replace(valueReferenceRegEx, parameter);
              }

              // e.g. stringReplace('${test}',['replaceFirstReference','replaceSecondReference'])
              if (isArray(parameter)) {
                  return parameter.reduce((reduceStringList, value) => {
                      if (!isString(reduceStringList)) {
                          return '';
                      }

                      return reduceStringList.replace(valueReferenceRegEx, value);
                  }, reduceString);
              }

              // e.g. stringReplace('${test}',{test,'replace'})
              if (isObject(parameter)) {
                  return Object.entries(parameter).reduce((reduceStringObject, [key, value]) => {
                      if (!isString(reduceStringObject)) {
                          return '';
                      }

                      return replaceAll(
                          reduceStringObject,
                          `\${${key}}`,
                          isString(value) ? value : String(value),
                      );
                  }, reduceString);
              }

              // any invalid case
              return reduceString;
          }, template) as string)
        : template;

export const tryGetErrorMessage = (error: unknown, language: Language) => {
    return error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : translations[language].ERROR_unknown;
};

export const getStringValue = (value: OptionValueType) => {
    switch (typeof value) {
        case 'string':
        case 'number':
        case 'boolean':
            return value.toString();
        default:
            return '';
    }
};

export const isNullOrEmpty = (value: OptionValueType | null | undefined) => {
    // Empty is null, undefined, '' or []
    // Whitespace is not empty
    return (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value === '') ||
        (Array.isArray(value) && value.length === 0)
    );
};

export const uniqueConnectorIdsFromActions = (actions: Action[]): string[] => {
    const connectorIds: string[] = actions.map((action) => action.providerId);
    return Array.from(new Set(connectorIds));
};

export const isTaskAuthenticated = (
    task: AutomationTaskEdit,
    status: AuthenticationStatusResponse,
) => {
    if (task.trigger && !status[task.trigger.providerId]?.[task.trigger.id]) {
        return false;
    }

    for (const action of task.actions) {
        if (!status[action.providerId]?.[action.id]) {
            return false;
        }
    }

    return true;
};

export const getConnectorIdsFromTasks = (tasks: AutomationTaskEdit[]) => {
    const connectorIds = tasks
        .flatMap((task) => {
            return [task?.trigger as TaskItem].concat(task?.actions as TaskItem[]);
        })
        .filter((item) => !!item)
        .map((item) => item.providerId);

    return connectorIds.filter(
        (connectorId, index) => connectorId && connectorIds.indexOf(connectorId) === index,
    );
};

export const isTaskItemAuthenticated = (
    item: TaskItem,
    authenticationStatus: AuthenticationStatusResponse,
) => {
    if (authenticationStatus[item.providerId]) {
        return !!authenticationStatus[item.providerId][item.id];
    }

    return false;
};

export const checkValidationRules = (
    validations: Validation[],
    values: Record<string, OptionValueType>,
) => {
    for (const validation of validations) {
        if (!apply(validation.rules, values)) {
            return validation.message;
        }
    }

    return null;
};

export const getValidationMessage = (
    option: Option,
    value: OptionValueType,
    language: Language,
) => {
    if (!option.isRequired && isNullOrEmpty(value)) {
        // allow not required fields to be empty
        return null;
    }

    if (option.isRequired && isNullOrEmpty(value)) {
        return translations[language].COMMON_field_required;
    }

    if (
        option.type === 'PhoneNumber' &&
        typeof value === 'string' &&
        !/^\+[1-9]\d{1,14}$/.test(value)
    ) {
        return translations[language].TASK_validation_phone_number;
    }

    return null;
};

export const getLocale = () => {
    const locale = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

    try {
        Intl.DateTimeFormat.supportedLocalesOf(locale);
        return locale;
    } catch (_error) {
        return 'en-US';
    }
};

export const isConfigValid = (configData: Trigger | Action, language: Language) => {
    const { validations, values, options } = configData;
    // Check if any required options are missing/phone numbers are valid.
    const basicValidationFailed = options.some((option) =>
        getValidationMessage(option, values[option.id] ?? null, language),
    );

    if (basicValidationFailed) {
        return false;
    }

    // Check if any option fails additional validation based on provided JSON logic rules.
    const validationRulesMessage = checkValidationRules(validations, values);

    if (validationRulesMessage) {
        return false;
    }

    return true;
};

export const getUniqueConnectors = (items: Connector[]) => [
    ...new Map(items.map((item) => [item.id, item])).values(),
];

export const pickObjProps = <T extends {}, K extends string | number | symbol>(obj: T, keys: K[]) =>
    Object.fromEntries(keys.map((key) => [key, obj[key as unknown as keyof T]])) as {
        [key in K]: key extends keyof T ? T[key] : undefined;
    };

export const getAuthenticatedConnectors = (
    connectors: Record<string, Connector>,
    itemType: 'trigger' | 'action',
    authenticationStatus: AuthenticationStatusResponse,
) => {
    // No connectors, exit early.
    if (!Object.keys(connectors).length) {
        return [];
    }

    // No authenticationStatus, exit early.
    if (!Object.keys(authenticationStatus).length) {
        return [];
    }

    // Is every relevant trigger/action authentication status for this connector true?
    // If it is, add it to the fully authenticated list.
    const fullyAuthenticated: string[] = [];
    // We will make this limited to item type, so if trigger (or action) we only care
    // that all triggers (or actions) are authenticated and not all triggers and
    // all actions.
    const triggersOrActions = itemType === 'trigger' ? 'triggers' : 'actions';

    for (const connectorId in connectors) {
        //  Since the auth status response does not distinguish between
        //  triggers and actions, we have to filter the status response down to
        //  relevant triggers or actions only.
        const relevantTriggersOrActionsIds = Object.keys(
            connectors[connectorId][triggersOrActions],
        );
        const relevantAuthStatuses = Object.fromEntries(
            Object.entries(authenticationStatus[connectorId]).filter(([prop]) =>
                relevantTriggersOrActionsIds.includes(prop),
            ),
        );
        // Check if all relevant trigger or action statuses are authenticated.
        const isFullyAuthenticated = Object.values(relevantAuthStatuses).every((status) => status);

        isFullyAuthenticated && fullyAuthenticated.push(connectorId);
    }

    return fullyAuthenticated;
};

export const defaultValues = (
    options: Option[],
    values: Record<string, OptionValueType> | undefined,
) => {
    const newValues: Record<string, OptionValueType> = {};
    options.forEach((option) => {
        newValues[option.id] = values?.[option.id] ?? option.suggestedDefaultValue;
    });

    return newValues;
};
